import axiosClient from "./axiosClient";

const API_PATH = "/elearning";

/**
 * BE rewrite img src trong question HTML thành relative
 * `/elearning/book/pluginfile?...&sig=...`. Khi FE + BE khác host (vd
 * staging-lms-* trên DigitalOcean vs api service riêng), browser resolve
 * relative theo FE host → request rơi vào CRA catch-all (200 + SPA
 * shell) → ảnh trống. Prepend axios baseURL (= REACT_APP_API_URL) →
 * absolute URL trỏ đúng BE host. Cùng pattern với fetchBookChapterHtml.
 *
 * Mutates questions array in-place.
 */
function absolutizePluginfileUrls(questions) {
  if (!Array.isArray(questions)) return;
  const apiBase = axiosClient.defaults.baseURL || '';
  if (!apiBase) return;
  for (const q of questions) {
    if (q?.html && typeof q.html === 'string') {
      q.html = q.html.replace(
        /(src|href)="\/elearning\/book\/pluginfile/g,
        `$1="${apiBase}/elearning/book/pluginfile`,
      );
    }
  }
}

class ElearningApi {
  // Backend-proxied login. Replaces the direct moodleApi.getToken() →
  // /login/token.php call so the Moodle wstoken never reaches the browser.
  // Returns { sessionToken, user } extracted by axiosClient response interceptor.
  studentLogin = async (username, password) => {
    const res = await axiosClient.post(`${API_PATH}/login`, { username, password });
    // Backend wraps with ResponseHandler -> { message, data: { sessionToken, refreshToken, ... } }.
    // Callers want the inner payload directly.
    return res?.data ?? res;
  }

  // Logout — revoke refresh token ở BE để invalidate immediately. FE
  // gọi best-effort (.catch swallow) trong logout UX flow.
  studentLogout = async (refreshToken) => {
    const res = await axiosClient.post(`${API_PATH}/logout`, { refreshToken });
    return res?.data ?? res;
  }

  // ─── Quiz proxy (Phase 1B) ──────────────────────────────────────────
  // These replace the direct moodleApi.{startQuizAttempt,getAttemptData,
  // saveAttemptData,finishQuizAttempt,getQuizAttemptSummary,
  // getAttemptReview,getUserAttempts} calls. Each goes through
  // `Authorization: Bearer <sessionToken>` and is rate-limited per-student
  // on the backend.
  //
  // The backend wraps every response in `{ message, data: <moodle payload> }`
  // via ResponseHandler. Callers used to consume the Moodle payload
  // directly, so we unwrap here to keep the call sites unchanged.
  quizStartAttempt = async (quizId) => {
    const res = await axiosClient.post(`${API_PATH}/quiz/attempt`, { quizId });
    return res?.data ?? res;
  }
  quizGetAttemptData = async (attemptId, page = 0) => {
    const res = await axiosClient.get(`${API_PATH}/quiz/attempt/${attemptId}/data`, { params: { page } });
    const payload = res?.data ?? res;
    absolutizePluginfileUrls(payload?.questions);
    return payload;
  }
  quizSaveAnswers = async (attemptId, data) => {
    const res = await axiosClient.post(`${API_PATH}/quiz/attempt/${attemptId}/save`, { data });
    return res?.data ?? res;
  }
  quizFinishAttempt = async (attemptId, timeUp = false, courseId = undefined) => {
    const body = { timeUp };
    if (courseId != null) body.courseId = courseId;
    const res = await axiosClient.post(`${API_PATH}/quiz/attempt/${attemptId}/finish`, body);
    return res?.data ?? res;
  }
  quizGetAttemptSummary = async (attemptId) => {
    const res = await axiosClient.get(`${API_PATH}/quiz/attempt/${attemptId}/summary`);
    return res?.data ?? res;
  }
  quizGetAttemptReview = async (attemptId, page = -1) => {
    const res = await axiosClient.get(`${API_PATH}/quiz/attempt/${attemptId}/review`, { params: { page } });
    const payload = res?.data ?? res;
    absolutizePluginfileUrls(payload?.questions);
    return payload;
  }
  quizGetUserAttempts = async (quizId, status = 'all') => {
    const res = await axiosClient.get(`${API_PATH}/quiz/${quizId}/attempts`, { params: { status } });
    return res?.data ?? res;
  }

  // ─── Course / completion proxy (Phase 2A) ────────────────────────────
  // These replace direct moodleApi calls so the browser no longer needs a
  // Moodle wstoken for course-list and content reads. Each unwraps the
  // ResponseHandler envelope so callers see Moodle's payload shape, same
  // contract as the legacy moodleApi functions.
  myEnrolledCourses = async (classification = 'inprogress', limit = 0, offset = 0) => {
    const res = await axiosClient.get(`${API_PATH}/me/courses`, { params: { classification, limit, offset } });
    // Moodle returns { courses: [...], nextoffset }; legacy moodleApi
    // unwrapped to the courses array, so callers iterate with .map directly.
    const payload = res?.data ?? res;
    return Array.isArray(payload?.courses) ? payload.courses : payload;
  }
  courseContents = async (courseId) => {
    const res = await axiosClient.get(`${API_PATH}/courses/${courseId}/contents`);
    return res?.data ?? res;
  }
  courseById = async (courseId) => {
    const res = await axiosClient.get(`${API_PATH}/courses/${courseId}`);
    return res?.data ?? res;
  }
  completionStatus = async (courseIds = []) => {
    const res = await axiosClient.post(`${API_PATH}/me/completion`, { courseIds });
    return res?.data ?? res;
  }
  quizzesByCourses = async (courseIds = []) => {
    const res = await axiosClient.post(`${API_PATH}/quizzes/by-courses`, { courseIds });
    return res?.data ?? res;
  }

  // ─── Book chapter pluginfile proxy (Phase 2D follow-on) ────────────
  // FE truyền URL gốc Moodle pluginfile.php (KHÔNG kèm ?token=) → BE
  // attach wstoken server-side. Trả text HTML cho `index.html` hoặc
  // binary cho images (browser tự handle qua <img src>).
  fetchBookChapterHtml = async (fileurl) => {
    const res = await axiosClient.get(`${API_PATH}/book/pluginfile`, {
      params: { fileurl },
      responseType: 'text',
      // axios response interceptor unwrap res.data — pass-through cho
      // text response.
      transformResponse: [(data) => data],
    });
    // axiosClient.interceptor.response trả res.data → response interceptor
    // pattern; nhưng responseType='text' khiến `res` đã là string.
    const html = typeof res === 'string' ? res : (res?.data ?? '');

    // BE rewrite img src thành relative `/elearning/book/pluginfile?...`.
    // Khi FE + BE khác host (vd staging-lms-* trên DigitalOcean vs api
    // service riêng), browser resolve relative theo FE host → request
    // rơi vào CRA catch-all (200 + SPA shell), KHÔNG đến BE → ảnh trống.
    // Prepend baseURL của axios (= REACT_APP_API_URL) → tuyệt đối, đi đúng
    // BE host. KHÔNG dùng startsWith vì có cả `src="/..."` lẫn `href="/..."`.
    const apiBase = axiosClient.defaults.baseURL || '';
    if (apiBase) {
      return html.replace(
        /(src|href)="\/elearning\/book\/pluginfile/g,
        `$1="${apiBase}/elearning/book/pluginfile`,
      );
    }
    return html;
  }

  // ─── Video proxy (Phase 2B) ──────────────────────────────────────────
  videoInstance = async (videoId) => {
    const res = await axiosClient.get(`${API_PATH}/videos/${videoId}`);
    return res?.data ?? res;
  }
  videoView = async (cmId) => {
    const res = await axiosClient.get(`${API_PATH}/videos/views/${cmId}`);
    return res?.data ?? res;
  }
  // Heartbeat video: chỉ gửi vị trí + các ô 15s đã đi qua (`indices`) —
  // server tự đóng dấu mapa bằng giờ server và tự tính percent, trả về
  // { mapa, percent } để hiển thị. `cmid` bắt buộc (server đọc gốc Moodle
  // theo cmid).
  videoUpdateView = async (viewId, { currentTime, duration, indices, cmid, videoInstanceId } = {}) => {
    const res = await axiosClient.put(`${API_PATH}/videos/views/${viewId}`, {
      currentTime, duration, indices,
      ...(cmid != null ? { cmid } : {}),
      ...(videoInstanceId != null ? { videoInstanceId } : {}),
    });
    return res?.data ?? res;
  }

  // ─── Forum proxy (Phase 2C) ──────────────────────────────────────────
  forumDiscussions = async (forumId) => {
    const res = await axiosClient.get(`${API_PATH}/forums/${forumId}/discussions`);
    return res?.data ?? res;
  }
  forumDiscussionPosts = async (discussionId) => {
    const res = await axiosClient.get(`${API_PATH}/forums/discussions/${discussionId}/posts`);
    return res?.data ?? res;
  }
  forumDiscussionPost = async (postId) => {
    const res = await axiosClient.get(`${API_PATH}/forums/posts/${postId}`);
    return res?.data ?? res;
  }
  forumAddReply = async ({ postId, subject, message }) => {
    const res = await axiosClient.post(`${API_PATH}/forums/posts/${postId}/reply`, { subject, message });
    return res?.data ?? res;
  }

  // ─── Change my password (Phase 2D step 2) ────────────────────────────
  // Replaces the legacy moodleApi.getToken + changeUserPasswordByMoodleToken
  // pair. Auth via sessionToken; the BE verifies oldPassword by trading it
  // for a fresh wstoken under the hood, so the browser never sees one.
  changeMyPassword = async ({ username, oldPassword, newPassword }) => {
    const res = await axiosClient.post(`${API_PATH}/me/change-password`, {
      username, oldPassword, newPassword,
    });
    return res?.data ?? res;
  }
  forceChangeMyPassword = async ({ username, newPassword }) => {
    const res = await axiosClient.post(`${API_PATH}/me/force-change-password`, {
      username, newPassword,
    });
    return res?.data ?? res;
  }

  // initCenterElearning + createCourseUsers were copy-paste leftovers from
  // the admin app (apps/uniapp-frontend/src/api/elearningApi.js). The
  // student-facing lms-frontend never calls them — both POST routes are
  // gated server-side by `authorize([roles.ADMIN, roles.DRIVING_ADMIN])`
  // and would 401 from a student sessionToken anyway. Removed.

  getCoursesByCatId = async (catId, page, limit) => {
    const url = `${API_PATH}/course`;
    return axiosClient.get(url, { params: {
      field: 'category',
      value: catId,
      page: page,
      limit: limit,
    } });
  }

  getCoursesByCenter = async (center, page, limit) => {
    const url = `${API_PATH}/center/course`;
    return axiosClient.get(url, {
      params: {
        center
      }
    });
  }

  getStudentCourses = async (elearningUserId) => {
    const url = `${API_PATH}/my/course`;
    return axiosClient.get(url, { params: { elearningUserId } });
  }

  getCategoryByIds = async (categoryIds) => {
    const url = `${API_PATH}/category`;
    return axiosClient.get(url, { params: { categoryIds } });
  }

  getCourseContent = async (courseId) => {
    const url = `${API_PATH}/course/content`;
    return axiosClient.get(url, { params: { courseId } });
  }

  getCoursesContents = async (courseIds = null) => {
    const allContents = {};
    
    for (const id of courseIds) {
      try {
        const courseContentRes = await this.getCourseContent(id);
        allContents[id] = courseContentRes?.data;
      } catch (error) {
        allContents[id] = { error: `Lỗi: ${error.message}` };
      }
    }

    return allContents;
  };

  getCohortUsers = async (cohortId) => {
    const url = `${API_PATH}/cohort/user`;
    return axiosClient.get(url, { params: { cohortId } });
  }

  getCourseUsers = async (courseId, groupId) => {
    const url = `${API_PATH}/course/user`;
    return axiosClient.get(url, { params: { courseId, groupId } });
  }

  // createCourseUsers removed alongside initCenterElearning — admin-only
  // POST that the student app never calls. See note above getCoursesByCatId.

  getGroupUsers = async (groupId) => {
    const url = `${API_PATH}/group/user`;
    return axiosClient.get(url, { params: { groupId } });
  }

  getCourseReport = async (courseId) => {
    const url = `${API_PATH}/course/report`;
    return axiosClient.get(url, { params: { courseId } });
  }

  getUserByMoodleToken = async (moodleToken) => {
    // Phase 2D step 5: passing moodleToken is now optional. axiosClient
    // already attaches `Authorization: Bearer <sessionToken>`, which the
    // updated /elearning/user/moodle controller honours. We still ship
    // the query param when present for un-updated FE tabs whose only
    // auth signal is moodleToken.
    const url = `${API_PATH}/user/moodle`;
    const params = moodleToken ? { moodleToken } : {};
    return axiosClient.get(url, { params });
  }
  // Phase 2D step 5: explicit sessionToken-only bootstrap for the new FE.
  // Layout calls this after the proxy login so it doesn't need a
  // moodleToken in localStorage.
  getMyBootstrap = async () => {
    return axiosClient.get(`${API_PATH}/user/moodle`);
  }

  changeUserPasswordByMoodleToken = async (moodleToken, newPassword, oldPassword) => {
    const url = `${API_PATH}/user/change-password`;
    return axiosClient.post(url, { moodleToken, newPassword, oldPassword });
  }

  getModuleTime = async (moduleId) => {
    const url = `${API_PATH}/module/setting`;
    return axiosClient.get(url, { params: { moduleId } });
  }

  // Batch wrapper cho /module/settings/batch — BE trả về
  // { [moduleId]: [...settings] } cùng shape per-id, cho phép caller
  // gom N call Promise.all → 1 call. Dùng ở StudentElearningLayout
  // fetchBookTime + fetchBookModuleSettings (mỗi cái map N module).
  getBatchModuleTime = async (moduleIds) => {
    const url = `${API_PATH}/module/settings/batch`;
    return axiosClient.get(url, { params: { moduleIds } });
  }

  getUserCourseGrade = async (userIds) => {
    const url = `${API_PATH}/course/grade`;
    return axiosClient.get(url, { params: { userIds } });
  }

  getElearningActivityReport = async (lessonIds, userIds) => {
    const url = `${API_PATH}/activity/report`;
    return axiosClient.get(url, { params: { lessonIds, userIds } });
  }

  getElearningSetting = async (moodleCourseId, drivingType) => {
    const url = `${API_PATH}/setting`;
    return axiosClient.get(url, { params: { moodleCourseId, ...(drivingType && { drivingType }) } });
  }

  // (getCoursesByCenter previously declared twice — second copy removed.)

  getUserQuizAttempts = async (userId, quizId) => {
    const url = `${API_PATH}/quiz/attempts`;
    return axiosClient.get(url, { params: { userId, quizId } });
  }

  // Batch trên backend trả về { [quizId]: { attempts: [...] } }, mỗi attempt
  // có state bao gồm 'abandoned' nếu admin đã huỷ. Dùng cho list page để
  // không phải gọi N call.
  getBatchQuizAttempts = async (userId, quizIds) => {
    const url = `${API_PATH}/quiz/attempts/batch`;
    return axiosClient.get(url, { params: { userId, quizIds } });
  }

  // Anti-cheat tracking — best-effort, KHÔNG block flow HV. Caller
  // wrap .catch(). BE swallow internal error và trả 200 với { ok: false }.
  startQuizAttemptTracking = (quizId, attemptId) => {
    return axiosClient.post(`${API_PATH}/quiz-attempt/start`, { quizId, attemptId });
  }

  finishQuizAttemptTracking = (attemptId) => {
    return axiosClient.post(`${API_PATH}/quiz-attempt/finish`, { attemptId });
  }

  getUserQuizAttempt = async (attemptId) => {
    const url = `${API_PATH}/quiz/attempt`;
    return axiosClient.get(url, { params: { attemptId } });
  }

  saveStudentBookData = async (data) => {
    const url = `/elearning-student-data`;
    return axiosClient.post(url, data);
  }

  getStudentBookData = async (elearningUserId, chapterId, moduleId) => {
    const url = `/elearning-student-data`;
    return axiosClient.get(url, { params: { elearningUserId, chapterId, moduleId } });
  }

  getAllStudentBookData = async (elearningUserId) => {
    const url = `/elearning-student-data`;
    return axiosClient.get(url, { params: { elearningUserId } });
  }

  getAllBookDataByStudent = async (drivingStudent) => {
    const url = `/elearning-student-data`;
    return axiosClient.get(url, { params: { drivingStudent, limit: 1000 } });
  }

  getStudentBookDataToday = async (drivingStudent, startOfDay, endOfDay) => {
    const url = `/elearning-student-data`;
    return axiosClient.get(url, {
      params: {
        drivingStudent,
        'updatedAt[$gte]': startOfDay,
        'updatedAt[$lt]': endOfDay,
        limit: 1000,
      },
    });
  }
  getTimeExclusions = async (drivingStudentId) => {
    const url = `${API_PATH}/time-exclusion`;
    return axiosClient.get(url, { params: { drivingStudentId } });
  }

  // trackVideoView REMOVED: anti-cheat side-channel obsolete sau khi
  // BE proxy catch trọn mọi PUT /elearning/videos/views/:id. Token
  // upsert giờ chạy server-side trong proxyUpdateVideoView (truyền
  // cmid + videoInstanceId qua videoUpdateView({...}) là đủ).

  getMyTodayTime = async () => {
    const url = `${API_PATH}/my/today-time`;
    const sessionToken = localStorage.getItem('sessionToken');
    const moodleToken = localStorage.getItem('moodleToken');
    const params = {};
    if (moodleToken) params.moodleToken = moodleToken;
    if (sessionToken) {
      return axiosClient.get(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        params,
      });
    }
    return axiosClient.get(url, { params });
  }

  // Student-facing cabin booking entry point. BE guards on
  // Driving.elearningStatus === 'completed' (cron-synced). Response on
  // success: { redirectUrl, registrationCode, ... }. 403 + code='NOT_COMPLETED'
  // when the student hasn't finished all courses yet.
  // Tóm tắt hoàn thành do backend tính (giờ từng môn, trạng thái, giờ từng
  // hoạt động) — màn "Kết quả" chỉ hiển thị số này. fresh=true bỏ qua cache.
  getMyCompletionSummary = async (fresh = false) => {
    const url = `${API_PATH}/my/completion-summary`;
    return axiosClient.get(url, { params: fresh ? { fresh: 1 } : {} });
  }

  requestCabinBooking = async () => {
    const url = `${API_PATH}/my/request-cabin-booking`;
    return axiosClient.get(url);
  }

  // Server-side submit gate — thay cho client compute `latestTotal +
  // wouldAdd > latestLimit` (bypass-able). BE fetch attempt summary +
  // today time, tự tính → trả {allowed, maxQuestionsAllowed, ...}.
  quizSubmitGate = async (attemptId, courseId) => {
    const url = `${API_PATH}/my/quiz-submit-gate`;
    const sessionToken = localStorage.getItem('sessionToken');
    const moodleToken = localStorage.getItem('moodleToken');
    const params = { attemptId, courseId };
    if (moodleToken) params.moodleToken = moodleToken;
    if (sessionToken) {
      return axiosClient.get(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        params,
      });
    }
    return axiosClient.get(url, { params });
  }

  // Log DevTools detection event tới backend. Best-effort — caller bỏ qua lỗi.
  logDevtoolsEvent = (payload) => {
    return axiosClient.post(`${API_PATH}/devtools-event`, payload);
  }
}

export default new ElearningApi();
